import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Path segment to display label (for known routes).
 * Add entries here to customize breadcrumb labels; unknown segments are auto-formatted.
 */
const SEGMENT_LABELS = {
  rbac: 'RBAC',
  'user-management': 'User Management',
  'roles-permissions': 'Roles & Permissions',
  hierarchy: 'Organization Hierarchy',
  dashboard: 'Dashboard',
  investors: 'Investors',
  list: 'List',
  create: 'Create',
  edit: 'Edit',
  'product-master': 'Product Master',
  'fixed-deposit': 'Fixed Deposit',
  'pre-ipo': 'Pre-IPO',
  reporting: 'Reporting',
  inventory: 'Inventory',
  listings: 'Listings',
  'order-management': 'Order Management',
  'basket-order-management': 'Basket Order Management',
  place: 'Place Order',
  attributes: 'Attribute Mapping',
  'attribute-catalog': 'Attribute Catalog',
  update: 'Update',
  ifa: 'Partner',
  pricing: 'Pricing',
  groups: 'Groups',
  'price-chart': 'Share Price Chart',
  news: 'News',
  notifications: 'Notifications',
  commissions: 'Commissions',
  introducer: 'Introducer',
  config: 'Configuration',
  banners: 'Banners',
  'tracxn-upload': 'Tracxn Upload',
  'reset-password': 'Reset Password',
  'basket-management': 'Basket Management',
  'purchase-deal-sheet': 'Purchase Deal Sheet',
  'trade-commitments': 'Trade Commitments',
  masters: 'Masters',
  'supplier-masters': 'Supplier Masters',
  suppliers: 'Suppliers',
  onboard: 'Onboard',
};

function formatSegment(segment) {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  return segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build breadcrumb items from current path (dynamic).
 * @param {string} pathname - e.g. /rbac/user-management
 * @returns {{ path: string, label: string }[]}
 */
function pathToBreadcrumbs(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];
  const items = [];
  let acc = '';
  for (let i = 0; i < segments.length; i++) {
    acc += (acc ? '/' : '') + segments[i];
    const isLast = i === segments.length - 1;
    items.push({
      path: '/' + acc,
      label: formatSegment(segments[i]),
      isLast,
    });
  }
  return items;
}

/**
 * Minimal page breadcrumbs. Dynamic from URL by default; optional `items` override.
 * @param {{ items?: { path: string, label: string }[] }} props
 */
export default function PageBreadcrumbs({ items: itemsProp }) {
  const { pathname } = useLocation();
  const items = itemsProp && itemsProp.length > 0
    ? itemsProp.map((item, i) => ({
        ...item,
        path: item.path,
        label: item.label,
        isLast: i === itemsProp.length - 1,
      }))
    : pathToBreadcrumbs(pathname);

  if (items.length === 0) return null;

  return (
    <div className="border-b border-gray-100 w-full">
      <nav
        className="flex items-center gap-1 py-1.5 px-3 sm:px-4 text-xs text-gray-500 min-h-0"
        aria-label="Breadcrumb"
      >
        {items.map((item, i) => (
        <span key={item.path} className="flex items-center gap-1">
          {i > 0 && <span className="text-gray-300">/</span>}
          {i === 0 || item.isLast ? (
            <span className={item.isLast ? 'font-medium text-gray-700 truncate' : 'text-gray-500 truncate'}>
              {item.label}
            </span>
          ) : (
            <Link
              to={item.path}
              className="hover:text-gray-900 truncate transition-colors"
            >
              {item.label}
            </Link>
          )}
        </span>
      ))}
      </nav>
    </div>
  );
}
